const SITE_NAME = 'ThousandYearKingdom.org';
const DEFAULT_DESCRIPTION =
  'ThousandYearKingdom.org helps believers form communities, organize projects, share needs, and collaborate on Christ-centered solutions.';
const DEFAULT_ORIGIN = 'https://thousandyearkingdom.org';

const ROUTE_METADATA = [
  {
    pattern: /^\/(?:public)?$/,
    title: SITE_NAME,
    description: 'Form communities, organize projects, and collaborate on Christ-centered solutions.',
  },
  {
    pattern: /^\/forum(?:\/|$)/,
    title: `Forum | ${SITE_NAME}`,
    description: 'Join community conversations, share updates, and coordinate around shared needs.',
  },
  {
    pattern: /^\/learn(?:\/|$)/,
    title: `Learn | ${SITE_NAME}`,
    description: 'Understand the TYK mission, tools, shared-discernment workflow, and conflict resolution process.',
  },
  {
    pattern: /^\/media(?:\/|$)/,
    title: `Media | ${SITE_NAME}`,
    description: 'Discover testimonies, world needs, discernment tools, learning resources, and Kingdom economy media.',
  },
  {
    pattern: /^\/group\/[^/]+/,
    title: `Group profile | ${SITE_NAME}`,
    description: 'Review a group mission, membership details, and public community profile.',
  },
  {
    pattern: /^\/group-members\/[^/]+/,
    title: `Group workspace | ${SITE_NAME}`,
    description: 'Coordinate group work, discussions, decisions, positions, and treasury visibility.',
  },
  {
    pattern: /^\/(?:marketplace|for-sale)(?:\/|$)/,
    title: `Marketplace | ${SITE_NAME}`,
    description: 'Browse community marketplace listings, trades, services, and asset opportunities.',
  },
  {
    pattern: /^\/jobs(?:\/|$)/,
    title: `Jobs | ${SITE_NAME}`,
    description: 'Find open community roles and apply with a profile-bound resume.',
  },
  {
    pattern: /^\/services(?:\/|$)/,
    title: `Services | ${SITE_NAME}`,
    description: 'Discover services offered by members and community partners.',
  },
  {
    pattern: /^\/real-estate(?:\/|$)/,
    title: `Real estate | ${SITE_NAME}`,
    description: 'Review community real-estate listings and opportunities.',
  },
  {
    pattern: /^\/dashboard(?:\/|$)/,
    title: `Dashboard | ${SITE_NAME}`,
    description: 'View your personal activity, alerts, votes, trades, and community progress.',
  },
  {
    pattern: /^\/conflicts\/[^/]+/,
    title: `Conflict resolution | ${SITE_NAME}`,
    description: 'Review conflict status, resolver panels, solution pieces, and resolution progress.',
  },
  {
    pattern: /^\/actions\/[^/]+\/refinement/,
    title: `Action refinement | ${SITE_NAME}`,
    description: 'Refine action plans, materials, milestones, risks, and funding readiness.',
  },
  {
    pattern: /^\/profile-setup(?:\/|$)/,
    title: `Profile setup | ${SITE_NAME}`,
    description: 'Complete the profile details needed to participate safely in community workflows.',
  },
  {
    pattern: /^\/settings(?:\/|$)/,
    title: `Settings | ${SITE_NAME}`,
    description: 'Manage account, notification, and participation preferences.',
  },
];

function normalizePath(pathname) {
  if (!pathname || typeof pathname !== 'string') {
    return '/';
  }

  const [withoutQuery] = pathname.split(/[?#]/);
  return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
}

function getCanonicalOrigin() {
  const configuredUrl = process.env.REACT_APP_PUBLIC_SITE_URL;
  if (!configuredUrl) {
    return DEFAULT_ORIGIN;
  }

  return configuredUrl.replace(/\/+$/, '');
}

function findMetaElement(selector, createElement) {
  const existing = document.head.querySelector(selector);
  if (existing) {
    return existing;
  }

  const element = createElement();
  document.head.appendChild(element);
  return element;
}

export function getRouteSeoMetadata(pathname) {
  const normalizedPath = normalizePath(pathname);
  const matched = ROUTE_METADATA.find(({ pattern }) => pattern.test(normalizedPath));
  const title = matched?.title || `Page not found | ${SITE_NAME}`;
  const description = matched?.description || DEFAULT_DESCRIPTION;
  const canonicalPath = matched ? normalizedPath : '/';
  const canonicalUrl = `${getCanonicalOrigin()}${canonicalPath === '/' ? '/' : canonicalPath}`;

  return {
    title,
    description,
    canonicalUrl,
    siteName: SITE_NAME,
  };
}

export function applyRouteSeoMetadata(pathname) {
  if (typeof document === 'undefined') {
    return getRouteSeoMetadata(pathname);
  }

  const metadata = getRouteSeoMetadata(pathname);
  document.title = metadata.title;

  const description = findMetaElement('meta[name="description"]', () => {
    const element = document.createElement('meta');
    element.setAttribute('name', 'description');
    return element;
  });
  description.setAttribute('content', metadata.description);

  const canonical = findMetaElement('link[rel="canonical"]', () => {
    const element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    return element;
  });
  canonical.setAttribute('href', metadata.canonicalUrl);

  for (const [property, content] of [
    ['og:title', metadata.title],
    ['og:description', metadata.description],
    ['og:url', metadata.canonicalUrl],
  ]) {
    const element = findMetaElement(`meta[property="${property}"]`, () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      return meta;
    });
    element.setAttribute('content', content);
  }

  for (const [name, content] of [
    ['twitter:title', metadata.title],
    ['twitter:description', metadata.description],
  ]) {
    const element = findMetaElement(`meta[name="${name}"]`, () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', name);
      return meta;
    });
    element.setAttribute('content', content);
  }

  return metadata;
}
